import type { FC } from 'react';
import { Badge } from '@components/ui/badge';
import type { RenewalSummaryView } from '@responses/renewal-summary';
import { formatDate } from '@root/helpers/date';

import { parseSummaryText } from '../utils/parse-summary';

interface SummaryStateProps {
  summary: RenewalSummaryView;
}

/**
 * The saved summary: AI tag with its timestamp, then the text rendered from
 * its own conventions — the first heading as the title, later headings as
 * group titles, "- " lines as bullets, the rest as paragraphs.
 */
export const SummaryState: FC<SummaryStateProps> = ({ summary }) => {
  const blocks = parseSummaryText(summary.content);
  const generatedAt = formatDate(summary.generated_at, 'D MMM YYYY [at] HH:mm');
  let headingsSeen = 0;

  return (
    <div className="flex flex-col gap-4 px-6 py-5 text-sm text-gray-700" data-testid="renewal-summary-content">
      <Badge variant="secondary" className="bg-violet-50 text-violet-700">
        AI-generated{generatedAt ? `: ${generatedAt}` : ''}
      </Badge>
      {summary.is_outdated && (
        <p className="text-xs text-amber-700" data-testid="renewal-summary-outdated">
          The contract has changed since this summary was written. Regenerate to bring it up to date.
        </p>
      )}
      {blocks.map((block, index) => {
        // Blocks are plain text and may repeat; position is their identity.
        if (block.type === 'heading') {
          headingsSeen += 1;
          return headingsSeen === 1 ? (
            <h3 key={index} className="font-semibold text-gray-900">
              {block.text}
            </h3>
          ) : (
            <h4 key={index} className="-mb-2.5 font-semibold text-gray-900">
              {block.text}
            </h4>
          );
        }
        if (block.type === 'list') {
          return (
            <ul key={index} className="list-disc space-y-1 pl-5 leading-relaxed">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{item}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={index} className="leading-relaxed">
            {block.text}
          </p>
        );
      })}
    </div>
  );
};
